import React, { useEffect, useState } from "react";

// Minimal drop-in replacement for react-toastify (~30KB saved).
// Supports the only API used in the app: toast.success/error/warning/info(message)
// and a <ToastContainer /> rendered once in App.js.

let listeners = [];
let idCounter = 0;

const emit = (type, message) => {
  const id = ++idCounter;
  listeners.forEach((fn) => fn({ id, type, message }));
};

export const toast = {
  success: (message) => emit("success", message),
  error: (message) => emit("error", message),
  warning: (message) => emit("warning", message),
  info: (message) => emit("info", message),
};

const ICONS = {
  success: { symbol: "✓", color: "#22c55e" },
  error: { symbol: "✕", color: "#ef4444" },
  warning: { symbol: "!", color: "#f59e0b" },
  info: { symbol: "i", color: "#3b82f6" },
};

const AUTO_CLOSE_MS = 2500;

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const onToast = (item) => {
      setToasts((prev) =>
        // Tapping a blocked action repeatedly used to stack a fresh copy of the same
        // message each time, burying the page under identical toasts. Saying it once
        // is the whole point of saying it.
        prev.some((t) => t.type === item.type && t.message === item.message)
          ? prev
          : [item, ...prev]
      );

      // Armed unconditionally, which is safe: a suppressed duplicate was never added,
      // so removing its id is a no-op and the visible toast keeps its own timer.
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== item.id));
      }, AUTO_CLOSE_MS);
    };
    listeners.push(onToast);
    return () => {
      listeners = listeners.filter((fn) => fn !== onToast);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "center",
        pointerEvents: "none",
        width: "min(90vw, 400px)",
      }}
    >
      {toasts.map(({ id, type, message }) => {
        const icon = ICONS[type] || ICONS.info;
        return (
          <div
            key={id}
            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== id))}
            style={{
              background: "#020618",
              color: "#fff",
              borderRadius: "12px",
              padding: "12px 16px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
              fontSize: "14px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: 10,
              pointerEvents: "auto",
              cursor: "pointer",
              maxWidth: "100%",
              animation: "diil-toast-in 0.25s ease",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: icon.color,
                fontWeight: 700,
                flexShrink: 0,
                lineHeight: 1,
              }}
            >
              {icon.symbol}
            </span>
            <span style={{ wordBreak: "break-word" }}>{message}</span>
          </div>
        );
      })}
      <style>{`@keyframes diil-toast-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};
